import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { salesOrderService } from '../../services/api';

// Initial state
const initialState = {
  salesOrders: [],
  salesOrder: null,
  items: [],
  activities: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchSalesOrders = createAsyncThunk(
  'salesOrders/fetchSalesOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.getSalesOrders(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSalesOrder = createAsyncThunk(
  'salesOrders/fetchSalesOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.getSalesOrder(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createSalesOrder = createAsyncThunk(
  'salesOrders/createSalesOrder',
  async (data, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.createSalesOrder(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSalesOrder = createAsyncThunk(
  'salesOrders/updateSalesOrder',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.updateSalesOrder(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSalesOrder = createAsyncThunk(
  'salesOrders/deleteSalesOrder',
  async (id, { rejectWithValue }) => {
    try {
      await salesOrderService.deleteSalesOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSalesOrderItems = createAsyncThunk(
  'salesOrders/fetchSalesOrderItems',
  async (id, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.getSalesOrderItems(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchSalesOrderActivities = createAsyncThunk(
  'salesOrders/fetchSalesOrderActivities',
  async (id, { rejectWithValue }) => {
    try {
      const response = await salesOrderService.getSalesOrderActivities(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Sales Order slice
const salesOrderSlice = createSlice({
  name: 'salesOrders',
  initialState,
  reducers: {
    clearSalesOrderError: (state) => {
      state.error = null;
    },
    clearSalesOrder: (state) => {
      state.salesOrder = null;
      state.items = [];
      state.activities = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch sales orders
      .addCase(fetchSalesOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salesOrders = action.payload.results || action.payload;
        // Handle pagination if API returns pagination data
        if (action.payload.count !== undefined) {
          state.pagination.totalCount = action.payload.count;
        }
        state.error = null;
      })
      .addCase(fetchSalesOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch sales orders';
      })
      // Fetch single sales order
      .addCase(fetchSalesOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salesOrder = action.payload;
        state.error = null;
      })
      .addCase(fetchSalesOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch sales order';
      })
      // Create sales order
      .addCase(createSalesOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSalesOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salesOrders.unshift(action.payload);
        state.error = null;
      })
      .addCase(createSalesOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create sales order';
      })
      // Update sales order
      .addCase(updateSalesOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSalesOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the sales order in the list if it exists
        state.salesOrders = state.salesOrders.map((salesOrder) =>
          salesOrder.id === action.payload.id ? action.payload : salesOrder
        );
        // Update the current sales order if it's the one being viewed
        if (state.salesOrder && state.salesOrder.id === action.payload.id) {
          state.salesOrder = action.payload;
        }
        state.error = null;
      })
      .addCase(updateSalesOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update sales order';
      })
      // Delete sales order
      .addCase(deleteSalesOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSalesOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salesOrders = state.salesOrders.filter(
          (salesOrder) => salesOrder.id !== action.payload
        );
        // Clear the current sales order if it's the one being deleted
        if (state.salesOrder && state.salesOrder.id === action.payload) {
          state.salesOrder = null;
        }
        state.error = null;
      })
      .addCase(deleteSalesOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete sales order';
      })
      // Fetch sales order items
      .addCase(fetchSalesOrderItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesOrderItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchSalesOrderItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch sales order items';
      })
      // Fetch sales order activities
      .addCase(fetchSalesOrderActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesOrderActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activities = action.payload;
        state.error = null;
      })
      .addCase(fetchSalesOrderActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch sales order activities';
      });
  },
});

export const { clearSalesOrderError, clearSalesOrder } = salesOrderSlice.actions;
export default salesOrderSlice.reducer;
